import React from "react";
import { Helmet } from "react-helmet-async";

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  author = "Worga Naturals",
  // Product-specific props
  productSchema = false,
  price,
  currency = "USD",
  availability = "InStock",
  brand = "Worga Naturals",
  category,
  sku,
}) => {
  const siteUrl = "https://worganaturals.com";
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const ogImage = image || `${siteUrl}/images/og-image.jpg`;

  const defaultTitle =
    "Worga Naturals - Premium Organic Products from Sri Lanka | Best from Nature";
  const defaultDescription =
    "Discover premium organic products crafted from Sri Lanka's finest harvests. Pure, traceable, and responsibly made with certified organic farmers.";
  const defaultKeywords =
    "organic products Sri Lanka, premium organic food, Sri Lankan organic fruits, natural preserves, certified organic farmers";

  // Product Schema for product pages
  const productSchemaData = productSchema
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: title || defaultTitle,
        description: description || defaultDescription,
        image: ogImage,
        brand: {
          "@type": "Brand",
          name: brand,
        },
        offers: {
          "@type": "Offer",
          url: fullUrl,
          priceCurrency: currency,
          price: price || "0",
          availability: `https://schema.org/${availability}`,
          seller: {
            "@type": "Organization",
            name: "Worga Naturals",
          },
        },
        ...(category && { category }),
        ...(sku && { sku }),
      }
    : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title ? `${title} | Worga Naturals` : defaultTitle}</title>
      <meta name="title" content={title || defaultTitle} />
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content={author} />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title || defaultTitle} />
      <meta
        property="og:description"
        content={description || defaultDescription}
      />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Worga Naturals" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title || defaultTitle} />
      <meta
        name="twitter:description"
        content={description || defaultDescription}
      />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:creator" content="@worganaturals" />

      {/* Product Schema (if enabled) */}
      {productSchema && productSchemaData && (
        <script type="application/ld+json">
          {JSON.stringify(productSchemaData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
